import { formatDistanceToNow } from 'date-fns';
import {
  Activity,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  BarChart3,
  Bell,
  CheckCircle,
  Clock,
  Download,
  ExternalLink,
  FileText,
  MoreVertical,
  RefreshCw,
  Search,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { toast } from 'sonner';
import { BureauAlertsWidget } from '@/components/credit-scoring/bureau-alerts-widget';
import { CreditScoreDetailSheet } from '@/components/credit-scoring/credit-score-detail-sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  mockBureauAlerts,
  mockCreditScoreDetails,
  mockCreditScores,
  mockDashboardMetrics,
} from '@/lib/mock-credit-data';
import { useSessionSortPreference } from '@/hooks/useSessionSortPreference';
import type { BureauSyncStatus, CreditScore, RiskLevel } from '@/types/credit-scoring';

type SortField = keyof CreditScore;
type SortDirection = 'asc' | 'desc';

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const getScoreBadgeColor = (score: number): string => {
  if (score >= 750) return 'bg-green-500 text-white';
  if (score >= 650) return 'bg-blue-500 text-white';
  if (score >= 550) return 'bg-yellow-500 text-white';
  return 'bg-red-500 text-white';
};

const getRiskBadgeVariant = (risk: RiskLevel): 'default' | 'secondary' | 'destructive' => {
  switch (risk) {
    case 'Low':
      return 'default';
    case 'Medium':
      return 'secondary';
    case 'High':
    case 'Critical':
      return 'destructive';
  }
};

const getSyncStatusBadge = (status: BureauSyncStatus, lastUpdated: Date) => {
  const hoursAgo = (Date.now() - lastUpdated.getTime()) / (1000 * 60 * 60);

  if (status === 'syncing') {
    return (
      <Badge variant="outline" className="gap-1">
        <RefreshCw className="h-3 w-3 animate-spin" />
        Syncing
      </Badge>
    );
  }
  if (status === 'error') {
    return (
      <Badge variant="destructive" className="gap-1">
        <AlertTriangle className="h-3 w-3" />
        Error
      </Badge>
    );
  }
  if (hoursAgo < 6) {
    return (
      <Badge className="bg-green-500 gap-1">
        <CheckCircle className="h-3 w-3" />
        Synced
      </Badge>
    );
  }
  if (hoursAgo < 24) {
    return (
      <Badge variant="secondary" className="gap-1">
        <Clock className="h-3 w-3" />
        Stale
      </Badge>
    );
  }
  return (
    <Badge variant="destructive" className="gap-1">
      <AlertTriangle className="h-3 w-3" />
      Outdated
    </Badge>
  );
};

export function CreditScoringPage() {
  const [creditScores, setCreditScores] = useState<CreditScore[]>(mockCreditScores);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [riskFilter, setRiskFilter] = useState<string>('all');
  const [syncStatusFilter, setSyncStatusFilter] = useState<string>('all');
  const [{ column: sortField, direction: sortDirection }, setSort] = useSessionSortPreference<SortField, SortDirection>(
    'credit-scoring',
    'customerName',
    'asc',
  );
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isBulkSyncing, setIsBulkSyncing] = useState(false);

  const selectedCustomerDetail = selectedCustomerId ? mockCreditScoreDetails[selectedCustomerId] : null;

  // Score distribution data for pie chart
  const scoreDistributionData = [
    { name: 'Excellent (750+)', value: mockDashboardMetrics.scoreDistribution.excellent, color: '#10b981' },
    { name: 'Good (650-749)', value: mockDashboardMetrics.scoreDistribution.good, color: '#3b82f6' },
    { name: 'Fair (550-649)', value: mockDashboardMetrics.scoreDistribution.fair, color: '#f59e0b' },
    { name: 'Poor (<550)', value: mockDashboardMetrics.scoreDistribution.poor, color: '#ef4444' },
  ];

  // Risk trend data for line chart (mock 12 months)
  const riskTrendData = [
    { month: 'Jan', avgScore: 655, highRisk: 3 },
    { month: 'Feb', avgScore: 652, highRisk: 3 },
    { month: 'Mar', avgScore: 648, highRisk: 2 },
    { month: 'Apr', avgScore: 645, highRisk: 2 },
    { month: 'May', avgScore: 650, highRisk: 2 },
    { month: 'Jun', avgScore: 653, highRisk: 2 },
    { month: 'Jul', avgScore: 651, highRisk: 2 },
    { month: 'Aug', avgScore: 649, highRisk: 2 },
    { month: 'Sep', avgScore: 647, highRisk: 2 },
    { month: 'Oct', avgScore: 648, highRisk: 2 },
    { month: 'Nov', avgScore: 648, highRisk: 2 },
    { month: 'Dec', avgScore: 648, highRisk: 2 },
  ];

  // Filtered and sorted scores
  const filteredAndSortedScores = useMemo(() => {
    const filtered = creditScores.filter((score) => {
      const matchesSearch =
        score.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        score.accountNumber.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRisk = riskFilter === 'all' || score.riskLevel === riskFilter;
      const matchesSync = syncStatusFilter === 'all' || score.bureauSyncStatus === syncStatusFilter;

      return matchesSearch && matchesRisk && matchesSync;
    });

    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle Date objects
      if (aValue instanceof Date && bValue instanceof Date) {
        aValue = aValue.getTime();
        bValue = bValue.getTime();
      }

      // Handle strings
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }

      // Handle numbers
      if (sortDirection === 'asc') {
        return (aValue as number) - (bValue as number);
      } else {
        return (bValue as number) - (aValue as number);
      }
    });

    return filtered;
  }, [creditScores, searchQuery, riskFilter, syncStatusFilter, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSort(field, sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(field, 'asc');
    }
  };

  const handleRefreshBureauData = (customerId: string) => {
    setIsRefreshing(true);
    toast.info('Syncing bureau data...');

    setTimeout(() => {
      setCreditScores((prev) =>
        prev.map((score) =>
          score.customerId === customerId
            ? { ...score, bureauLastUpdated: new Date(), bureauSyncStatus: 'synced' as BureauSyncStatus }
            : score,
        ),
      );
      setIsRefreshing(false);
      toast.success('Bureau data synced successfully');
    }, 2000);
  };

  const handleBulkSync = () => {
    setIsBulkSyncing(true);
    toast.info('Syncing all bureau data... This may take a few moments');

    setTimeout(() => {
      setCreditScores((prev) =>
        prev.map((score) => ({
          ...score,
          bureauLastUpdated: new Date(),
          bureauSyncStatus: 'synced' as BureauSyncStatus,
        })),
      );
      setIsBulkSyncing(false);
      toast.success(`All bureau data synced - Updated ${creditScores.length} customer records`);
    }, 3000);
  };

  const handleReviewAlert = (customerId: string) => {
    setSelectedCustomerId(customerId);
  };

  const handleViewAllAlerts = () => {
    toast.info('View all alerts feature coming soon');
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4 ml-1" />;
    return sortDirection === 'asc' ? <ArrowUp className="h-4 w-4 ml-1" /> : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold">Credit Intelligence & Risk Assessment</h1>
          </div>
          <p className="text-muted-foreground">Monitor customer creditworthiness and bureau data</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline">
            <FileText className="h-4 w-4 mr-2" />
            Generate Report
          </Button>
          <Button onClick={handleBulkSync} disabled={isBulkSyncing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isBulkSyncing ? 'animate-spin' : ''}`} />
            Sync All Bureau Data
          </Button>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Average Score */}
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              <CardTitle className="text-sm font-medium">Average Score</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-700">{mockDashboardMetrics.averageScore}</div>
            <p className="text-xs text-muted-foreground mt-1">Across {creditScores.length} customers</p>
          </CardContent>
        </Card>

        {/* High-Risk Accounts */}
        <Card className="bg-red-50 border-red-200">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <CardTitle className="text-sm font-medium">High-Risk Accounts</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-700">{mockDashboardMetrics.highRiskAccountsCount}</div>
            <p className="text-xs text-muted-foreground mt-1">Require immediate attention</p>
          </CardContent>
        </Card>

        {/* Recent Changes */}
        <Card className="bg-orange-50 border-orange-200">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <TrendingDown className="h-5 w-5 text-orange-600" />
              <CardTitle className="text-sm font-medium">Recent Changes</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-700">{mockDashboardMetrics.recentScoreChanges}</div>
            <p className="text-xs text-muted-foreground mt-1">Score changes last 30 days</p>
          </CardContent>
        </Card>

        {/* Pending Reviews */}
        <Card className="bg-purple-50 border-purple-200">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-purple-600" />
              <CardTitle className="text-sm font-medium">Pending Reviews</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-700">{mockDashboardMetrics.pendingReviews}</div>
            <p className="text-xs text-muted-foreground mt-1">Reviews overdue</p>
          </CardContent>
        </Card>

        {/* Bureau Status */}
        <Card className="bg-green-50 border-green-200">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-green-600" />
              <CardTitle className="text-sm font-medium">Bureau Status</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700 capitalize">
              {mockDashboardMetrics.bureauSyncStatus.syncHealth}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Last sync {formatDistanceToNow(mockDashboardMetrics.bureauSyncStatus.lastSync, { addSuffix: true })}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Score Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Score Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={scoreDistributionData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${(name ?? '').split(' ')[0]}: ${value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {scoreDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Risk Trend Analysis */}
        <Card>
          <CardHeader>
            <CardTitle>Risk Trend Analysis (12 Months)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={riskTrendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis yAxisId="left" domain={[600, 700]} />
                <YAxis yAxisId="right" orientation="right" domain={[0, 5]} />
                <Tooltip />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="avgScore"
                  stroke="#3b82f6"
                  name="Avg Score"
                  strokeWidth={2}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="highRisk"
                  stroke="#ef4444"
                  name="High-Risk Count"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Bureau Alerts Widget */}
      <BureauAlertsWidget alerts={mockBureauAlerts} onReviewAlert={handleReviewAlert} onViewAll={handleViewAllAlerts} />

      {/* Customer Credit Scoring Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Customer Credit Scores</CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
          {/* Filters */}
          <div className="flex gap-3 mt-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by customer name or account number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={riskFilter} onValueChange={setRiskFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Risk Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Risk Levels</SelectItem>
                <SelectItem value="Low">Low</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="High">High</SelectItem>
                <SelectItem value="Critical">Critical</SelectItem>
              </SelectContent>
            </Select>
            <Select value={syncStatusFilter} onValueChange={setSyncStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Sync Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="synced">Synced</SelectItem>
                <SelectItem value="stale">Stale</SelectItem>
                <SelectItem value="error">Error</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('customerName')}>
                    <div className="flex items-center">
                      Customer
                      <SortIcon field="customerName" />
                    </div>
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('internalScore')}>
                    <div className="flex items-center">
                      Internal Score
                      <SortIcon field="internalScore" />
                    </div>
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('bureauScore')}>
                    <div className="flex items-center">
                      Bureau Score
                      <SortIcon field="bureauScore" />
                    </div>
                  </TableHead>
                  <TableHead>Variance</TableHead>
                  <TableHead>Risk Level</TableHead>
                  <TableHead className="text-right">Credit Limit</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead className="text-right">Utilisation</TableHead>
                  <TableHead>DSO</TableHead>
                  <TableHead>Bureau Status</TableHead>
                  <TableHead>Alerts</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSortedScores.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center py-8 text-muted-foreground">
                      No customers found matching your filters
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAndSortedScores.map((score) => (
                    <TableRow key={score.customerId}>
                      <TableCell>
                        <button
                          onClick={() => setSelectedCustomerId(score.customerId)}
                          className="text-left hover:underline"
                        >
                          <div className="font-medium">{score.customerName}</div>
                          <div className="text-sm text-muted-foreground">{score.accountNumber}</div>
                        </button>
                      </TableCell>
                      <TableCell>
                        <Badge className={getScoreBadgeColor(score.internalScore)}>{score.internalScore}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Badge className={getScoreBadgeColor(score.bureauScore)}>{score.bureauScore}</Badge>
                          <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {score.scoreVariance > 0 ? (
                            <TrendingUp className="h-4 w-4 text-green-500" />
                          ) : (
                            <TrendingDown className="h-4 w-4 text-red-500" />
                          )}
                          <span className={score.scoreVariance < -50 ? 'text-red-600 font-semibold' : ''}>
                            {score.scoreVariance > 0 ? '+' : ''}
                            {score.scoreVariance}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getRiskBadgeVariant(score.riskLevel)}>{score.riskLevel}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(score.creditLimit)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(score.outstandingBalance)}</TableCell>
                      <TableCell className="text-right">
                        <div className="space-y-1">
                          <Progress value={score.utilizationRate} className="h-2 w-16" />
                          <span className="text-sm">{score.utilizationRate}%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{score.dso} days</span>
                      </TableCell>
                      <TableCell>{getSyncStatusBadge(score.bureauSyncStatus, score.bureauLastUpdated)}</TableCell>
                      <TableCell>
                        {score.hasBureauAlerts && (
                          <Badge variant="destructive" className="gap-1">
                            <Bell className="h-3 w-3" />
                            {score.bureauAlertCount}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => setSelectedCustomerId(score.customerId)}>
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleRefreshBureauData(score.customerId)}>
                              Refresh Bureau Data
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem>Adjust Credit Limit</DropdownMenuItem>
                            <DropdownMenuItem>Request Review</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Detail Sheet */}
      <CreditScoreDetailSheet
        customer={selectedCustomerDetail}
        open={!!selectedCustomerId}
        onOpenChange={(open) => !open && setSelectedCustomerId(null)}
        onRefreshBureauData={handleRefreshBureauData}
        isRefreshing={isRefreshing}
      />
    </div>
  );
}
